import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Ban, Users, Wifi } from 'lucide-react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Pagination,
  Select,
  Skeleton,
  type Column,
} from '../../components/ui';
import { api, compact } from '../../lib/api';
import { TYPING_DEBOUNCE_MS, useDebounced } from '../../lib/debounce';
import { formatDateTime } from '../../lib/format';
import { useT } from '../../lib/i18n';
import { errorMessage, useToast } from '../../lib/toast';
import type { Paginated, ReferralRedemption, ReferralStatus } from '../../lib/types';
import { CampaignSelect, REFERRAL_TONES, UserRef } from './shared';

/**
 * Who joined with which code — the core of "who used it". Every row names
 * both accounts, so a farm shows up as the same referrer, the same address or
 * the same minute repeated down the page.
 */
export function RedemptionsTab() {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [revoking, setRevoking] = useState<ReferralRedemption | null>(null);

  const filters = {
    campaignId: params.get('campaignId') ?? '',
    status: params.get('status') ?? '',
    code: params.get('code') ?? '',
    referrerDeviceId: params.get('referrerDeviceId') ?? '',
    refereeDeviceId: params.get('refereeDeviceId') ?? '',
  };
  // Typed a character at a time; one request per pause, not per keystroke.
  const code = useDebounced(filters.code.trim(), TYPING_DEBOUNCE_MS);
  const referrerDeviceId = useDebounced(filters.referrerDeviceId, TYPING_DEBOUNCE_MS);
  const refereeDeviceId = useDebounced(filters.refereeDeviceId, TYPING_DEBOUNCE_MS);

  const set = (key: keyof typeof filters, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setPage(1);
  };

  const query = useMemo(
    () =>
      compact({
        campaignId: filters.campaignId,
        status: filters.status,
        code,
        referrerDeviceId,
        refereeDeviceId,
        page,
        limit: 25,
      }),
    [filters.campaignId, filters.status, code, referrerDeviceId, refereeDeviceId, page],
  );

  const redemptions = useQuery({
    queryKey: ['referrals', 'redemptions', query],
    queryFn: () => api.get<Paginated<ReferralRedemption>>('/api/referrals/redemptions', query),
  });

  const revoke = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/api/referrals/redemptions/${id}/revoke`, { reason }),
    onSuccess: () => {
      toast.success(t('referrals.redemptions.revoked'));
      setRevoking(null);
      void queryClient.invalidateQueries({ queryKey: ['referrals'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const columns: Column<ReferralRedemption>[] = [
    {
      key: 'friend',
      header: t('referrals.redemptions.friend'),
      render: (row) => <UserRef id={row.refereeId} />,
    },
    {
      key: 'code',
      header: t('referrals.redemptions.code'),
      render: (row) => (
        <code className="font-mono text-xs font-semibold tracking-wider">{row.code}</code>
      ),
    },
    {
      key: 'campaign',
      header: t('referrals.redemptions.campaign'),
      render: (row) => (
        <span className="break-words">{row.campaignName ?? t('referrals.archived')}</span>
      ),
    },
    {
      key: 'referrer',
      header: t('referrals.redemptions.referrer'),
      render: (row) => <UserRef id={row.referrerId} />,
    },
    {
      key: 'status',
      header: t('referrals.redemptions.status'),
      render: (row) => (
        <span className="inline-flex flex-wrap items-center justify-end gap-1">
          <Badge tone={REFERRAL_TONES[row.status]}>
            {t(`referrals.referralStatus.${row.status}`)}
          </Badge>
          {row.sameIpAsReferrer && (
            <Badge tone="warn" icon={<Wifi size={11} />}>
              {t('referrals.redemptions.sameIp')}
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: 'redeemed',
      header: t('referrals.redemptions.redeemedAt'),
      render: (row) => formatDateTime(row.redeemedAt),
    },
    {
      key: 'qualified',
      header: t('referrals.redemptions.qualifiedAt'),
      desktopOnly: true,
      render: (row) => formatDateTime(row.qualifiedAt),
    },
    {
      key: 'ip',
      header: t('referrals.redemptions.ip'),
      desktopOnly: true,
      render: (row) => <span className="font-mono text-xs">{row.refereeIp ?? '—'}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (row) =>
        row.status === 'revoked' ? null : (
          <Button
            variant="ghost"
            className="text-xs text-danger"
            icon={<Ban size={13} />}
            onClick={() => setRevoking(row)}
          >
            {t('referrals.redemptions.revoke')}
          </Button>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-3 rounded-xl border border-line bg-surface p-3.5 sm:grid-cols-2 sm:p-4 xl:grid-cols-5">
        <CampaignSelect value={filters.campaignId} onChange={(value) => set('campaignId', value)} />
        <Field label={t('referrals.filter.status')}>
          <Select value={filters.status} onChange={(event) => set('status', event.target.value)}>
            <option value="">{t('referrals.filter.anyStatus')}</option>
            {(['pending', 'qualified', 'revoked'] as ReferralStatus[]).map((value) => (
              <option key={value} value={value}>
                {t(`referrals.referralStatus.${value}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('referrals.filter.code')}>
          <Input
            className="font-mono uppercase"
            value={filters.code}
            maxLength={32}
            onChange={(event) => set('code', event.target.value.trim())}
            placeholder="K7M2QX9P"
          />
        </Field>
        <Field label={t('referrals.filter.referrer')}>
          <Input
            className="font-mono text-xs"
            value={filters.referrerDeviceId}
            onChange={(event) => set('referrerDeviceId', event.target.value.trim())}
            placeholder={t('common.pasteDeviceId')}
          />
        </Field>
        <Field label={t('referrals.filter.friend')}>
          <Input
            className="font-mono text-xs"
            value={filters.refereeDeviceId}
            onChange={(event) => set('refereeDeviceId', event.target.value.trim())}
            placeholder={t('common.pasteDeviceId')}
          />
        </Field>
      </div>

      {redemptions.isError && <ErrorNote error={redemptions.error} />}

      <Card>
        {redemptions.isPending ? (
          <Skeleton className="h-96" />
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={redemptions.data?.items ?? []}
              rowKey={(row) => row.id}
              empty={
                <EmptyState
                  icon={<Users size={28} />}
                  title={t('referrals.redemptions.emptyTitle')}
                  description={t('referrals.redemptions.emptyDescription')}
                />
              }
            />
            {(redemptions.data?.items.length ?? 0) > 0 && (
              <Pagination
                page={redemptions.data?.page ?? 1}
                totalPages={redemptions.data?.totalPages ?? 1}
                total={redemptions.data?.total ?? 0}
                onChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <ConfirmDialog
        open={revoking !== null}
        title={t('referrals.redemptions.confirmTitle')}
        description={
          revoking
            ? t('referrals.redemptions.confirmDescription', { code: revoking.code })
            : undefined
        }
        confirmLabel={t('referrals.redemptions.revoke')}
        destructive
        loading={revoke.isPending}
        onCancel={() => setRevoking(null)}
        onConfirm={(reason) => revoking && revoke.mutate({ id: revoking.id, reason })}
      />
    </div>
  );
}
